import React from 'react';
import { isCaptureVisit } from '../../themeHost/captureScreen';
import { useHasMounted } from './useHasMounted';
import { createElement } from 'react';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { boolProp, choiceProp, numberProp } from './elementProps';
import { ELEMENT_FALLBACK_ICON, ELEMENT_ICONS } from './elementIcons';
import { realOf, useSlideshow, type SlideshowRun } from './useSlideshow';
import './elementSlideshow.css';
import { named, useShopNames } from '../../commerce/shopNames';

// A carousel of whatever a merchant put inside it: one child, one slide. Measured against Sleek's own
// hero, which is the shape this reproduces — the current slide held to about seven tenths of the width
// with its neighbours showing past both edges, and the dots in the flow beneath the picture rather than
// laid over it.
//
// The builder holds the carousel still while editing; Preview enables its storefront motion.

/** What a shopper is given to move between slides, exactly as `slideshowElement` offers it. */
const CONTROLS = ['dots', 'arrows', 'both', 'none'] as const;

/** How a slide's picture arrives, exactly as `slideshowElement` offers it. */
const MOTIONS = ['zoom-out', 'zoom-out-sm', 'none'] as const;

// Says whether one kind of control is drawn under the merchant's choice of what to show.
const shows = (choice: (typeof CONTROLS)[number], kind: 'dots' | 'arrows'): boolean =>
  choice === kind || choice === 'both';

// Draws the dots: one target per slide, the current one drawn as a pill and said out loud as well as shown.
const SlideDots: React.FC<{ run: SlideshowRun; count: number }> = ({ run, count }) => {
  const names = useShopNames();
  return (
    <div className="xe-slides-dots" role="tablist" aria-label={names.slides}>
      {Array.from({ length: count }, (_unused, at) => (
        <button
          key={at} type="button" role="tab" className="xe-slides-dot"
          aria-selected={at === run.index} aria-label={named(names.slideOf, { n: at + 1, count })}
          data-current={at === run.index || undefined} onClick={() => run.go(at)}
        >
          <span className="xe-slides-dot-track" />
        </button>
      ))}
    </div>
  );
};

// The arrows, laid over the picture, and hidden on a phone where a swipe is the way through.
const SlideArrows: React.FC<{ run: SlideshowRun }> = ({ run }) => {
  const Back = ELEMENT_ICONS['chevron-left'] ?? ELEMENT_FALLBACK_ICON;
  const names = useShopNames();
  const Forward = ELEMENT_ICONS['chevron-right'] ?? ELEMENT_FALLBACK_ICON;
  return (
    <>
      <button type="button" className="xe-slides-arrow" data-side="start" aria-label={names.previous} onClick={run.previous}>
        <Back size={20} strokeWidth={1.5} aria-hidden="true" />
      </button>
      <button type="button" className="xe-slides-arrow" data-side="end" aria-label={names.next} onClick={run.next}>
        <Forward size={20} strokeWidth={1.5} aria-hidden="true" />
      </button>
    </>
  );
};

// What the stylesheet needs from the merchant's settings: how wide a slide runs, and how long it holds.
const slideshowVars = (percent: number, holdSeconds: number): React.CSSProperties =>
  ({
    '--xe-slide-authored': `${Math.min(100, Math.max(30, percent))}%`,
    '--xe-slides-hold': `${Math.max(2, holdSeconds)}s`
  } as React.CSSProperties);

// Draws the carousel: the slides in one snapping row, the way between them, and the dots beneath.
export const SlideshowElement: ElementComponent = ({ node, children }) => {
  const box = useElementBox(node);
  const { editing, motionPreview } = useElementRender();
  const frozen = editing && !motionPreview;
  const slides = React.Children.toArray(children);
  const controls = choiceProp(node.props, 'controls', CONTROLS, 'dots');
  const motion = choiceProp(node.props, 'motion', MOTIONS, 'zoom-out');
  // The builder shows the carousel a shopper gets, held still: nothing moves on its own, and the loop's
  // copies are not drawn, because a copied slide is the same id twice on a page where every id is a
  // thing that can be selected. It used to stack the slides in a column instead, and a merchant looking
  // at two slides one under the other read the theme as broken — which is fair, since that is not the
  // theme. Picking a slide in the tree scrolls the run to it, the way any picked node is revealed.
  // A picture taken of the store for the themes screen holds it on its first slide too.
  const autoplay = !frozen && !isCaptureVisit() && boolProp(node.props, 'autoplay', true);
  const hold = numberProp(node.props, 'holdSeconds', 5);
  // The loop's copies join once the page is live: a page drawn on the server cannot be scrolled to its first slide, so it
  // starts there by having nothing before it, and the browser's first drawing matches it before the copies come in.
  const mounted = useHasMounted();
  const looping = !frozen && mounted && boolProp(node.props, 'loop', true) && slides.length > 1;
  const run = useSlideshow({
    count: slides.length,
    autoplay,
    holdSeconds: hold,
    pauseOnHover: boolProp(node.props, 'pauseOnHover', false),
    loop: looping
  });

  // A looping run is drawn with the last slide copied before the first and the first copied after the
  // last, so a neighbour always peeks on both sides. A copy is hidden from a screen reader: it is the
  // same slide said twice.
  const drawn = looping ? [slides[slides.length - 1], ...slides, slides[0]] : slides;
  const className = [box.className, 'xe-slides'].filter(Boolean).join(' ');

  return (
    <div
      {...box} className={className} data-motion={frozen || motion === 'none' ? undefined : motion} data-autoplay={autoplay || undefined}
      data-editing={frozen || undefined}
      data-paused={run.paused || undefined} data-opening={!run.moved || undefined}
      style={slideshowVars(numberProp(node.props, 'slideWidth', 100), hold)} {...run.hold}
    >
      <div className="xe-slides-frame">
        <div className="xe-slides-viewport" ref={run.viewport} aria-roledescription="carousel">
          {drawn.map((slide, at) => {
            const real = realOf(at, slides.length, looping);
            const copy = looping && (at === 0 || at === drawn.length - 1);
            // Each real slide keeps its own key as the copies come and go, so the picture a shopper is already
            // looking at is never torn down and drawn again (which also made it the page's largest paint twice).
            const key = copy ? (at === 0 ? 'copy-before' : 'copy-after') : `slide-${real}`;
            return (
              <div
                key={key} className="xe-slides-slide"
                data-current={real === run.index || undefined}
                aria-hidden={copy || undefined}
                // A loop's copy is unreachable by keyboard too; React 18 has no typed `inert`, so it is written as the attribute.
                {...(copy && !editing ? ({ inert: '' } as Record<string, string>) : {})}
              >
                {slide}
              </div>
            );
          })}
        </div>
        {slides.length > 1 && shows(controls, 'arrows') && createElement('div', { className: `xe-slides-controls${editing ? ' xs-editor-chrome' : ''}` }, <SlideArrows run={run} />)}
      </div>
      {slides.length > 1 && shows(controls, 'dots') && createElement('div', { className: editing ? 'xs-editor-chrome' : undefined }, <SlideDots run={run} count={slides.length} />)}
    </div>
  );
};
